import type { ConfigPapel, MedidasPagina } from '@miolos/core';
import { FURO_ATE_A_BORDA_MM, posicoesDosFuros } from '@miolos/core';

/**
 * Guia de furo impressa na margem da encadernação.
 *
 * Sai impressa de propósito — e por isso vem desligada por padrão. A marca fica
 * na margem da lombada e continua visível no miolo encadernado; ela serve para a
 * encadernadora alinhar o furo pela linha em vez de medir folha por folha. Quem
 * manda o arquivo para uma gráfica que já perfura no padrão dela não precisa
 * ligar isto.
 */
export function GuiaDeFuro({
  medidas,
  papel,
  lado,
}: {
  medidas: MedidasPagina;
  papel?: ConfigPapel;
  lado: 'ESQUERDO' | 'DIREITO';
}) {
  const encadernacao = papel?.encadernacao ?? 'NENHUMA';
  const comFuro = encadernacao === 'WIRE_O' || encadernacao === 'ESPIRAL';

  if (!papel?.guiaDeFuro || !comFuro || papel.ladoLombada === 'NENHUMA') return null;

  const topo = papel.ladoLombada === 'TOPO';
  const direita = !topo && lado === 'DIREITO';
  const comprimento = topo ? medidas.larguraMm : medidas.alturaMm;
  // Os mesmos centros que a encadernadora vai furar: linha e marca alinhadas.
  const centros = posicoesDosFuros(comprimento);
  if (centros.length < 3) return null;

  const linha = '#9aa3ad';
  const marcas = centros.map((centro) => (
    <span
      key={centro}
      style={{
        position: 'absolute',
        // O sinal de mais marca o centro do furo.
        ...(topo
          ? {
              left: `${centro}mm`,
              top: `${FURO_ATE_A_BORDA_MM}mm`,
              width: '4mm',
              height: '4mm',
              transform: 'translate(-50%, -50%)',
              backgroundImage: `linear-gradient(to right, transparent 0 1.6mm, ${linha} 1.6mm 2.4mm, transparent 2.4mm), linear-gradient(to bottom, transparent 0 1.6mm, ${linha} 1.6mm 2.4mm, transparent 2.4mm)`,
            }
          : {
              top: `${centro}mm`,
              [direita ? 'right' : 'left']: `${FURO_ATE_A_BORDA_MM}mm`,
              width: '4mm',
              height: '4mm',
              transform: 'translateY(-50%)',
              backgroundImage: `linear-gradient(to right, transparent 0 1.6mm, ${linha} 1.6mm 2.4mm, transparent 2.4mm), linear-gradient(to bottom, transparent 0 1.6mm, ${linha} 1.6mm 2.4mm, transparent 2.4mm)`,
            }),
      }}
    />
  ));

  // Linha tracejada ligando os furos: é por ela que a encadernadora alinha.
  const primeira = centros[0]!;
  const ultima = centros[centros.length - 1]!;
  const guia = topo
    ? {
        position: 'absolute' as const,
        left: `${primeira}mm`,
        right: `${Math.max(0, comprimento - ultima)}mm`,
        top: `${FURO_ATE_A_BORDA_MM}mm`,
        height: 0,
        borderTop: `0.15mm dashed ${linha}`,
      }
    : {
        position: 'absolute' as const,
        top: `${primeira}mm`,
        bottom: `${Math.max(0, comprimento - ultima)}mm`,
        [direita ? 'right' : 'left']: `${FURO_ATE_A_BORDA_MM}mm`,
        width: 0,
        borderLeft: `0.15mm dashed ${linha}`,
      };

  return (
    <div style={{ position: 'absolute', inset: 0 }} aria-hidden>
      <span style={guia} />
      {marcas}
    </div>
  );
}
